import React from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import moment from 'moment';
import { Card } from 'components/UI';
import Box from '@material-ui/core/Box';
import { Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  detailsListItem: {
    borderBottom: '1px solid #EEEEEE',
  },
  detailsListText: {
    fontSize: 13,
  },
  detailsListSecondText: {
    fontSize: 13,
    textAlign: 'right',
  },
  chipContainer: {
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
  tagsText: {
    marginTop: theme.spacing(0.85),
  },
}));

function DocumentDetailsWidget({ document, tags, onTagClick }) {
  const classes = useStyles();
  const { localeFormat } = useSelector((state) => state.organizations);

  return (
    <Card
      headerActionType="vertical"
      data-cy="documents-details-card"
      title={i18n.t('documents.document')}
    >
      <Box className={classes.detailsList}>
        {document?.created_time && (
          <Box
            pb={1}
            pt={1}
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            className={classes.detailsListItem}
          >
            <Typography variant="subtitle2" className={classes.detailsListText}>
              {i18n.t('documents.creation_date')}:
            </Typography>
            <Typography variant="body2" className={classes.detailsListSecondText}>
              {moment(document.created_time).format(localeFormat)}
            </Typography>
          </Box>
        )}
        {document.default?.expiration_time && (
          <Box
            pb={1}
            pt={1}
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            className={classes.detailsListItem}
          >
            <Typography variant="subtitle2" className={classes.detailsListText}>
              {`${i18n.t('documents.expiration_date')}:`}
            </Typography>
            <Typography variant="body2" className={classes.detailsListSecondText}>
              {moment(document.default.expiration_time).format(localeFormat)}
            </Typography>
          </Box>
        )}
        <Box
          pb={1}
          pt={1}
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          className={classes.detailsListItem}
        >
          <Typography variant="subtitle2" className={classes.detailsListText}>
            {`${i18n.t('common.name')}:`}
          </Typography>
          <Typography variant="body2" className={classes.detailsListSecondText}>
            {document?.document_type?.name || '-'}
          </Typography>
        </Box>
        {document.description && (
          <Box
            pb={1}
            pt={1}
            display="flex"
            justifyContent="space-between"
            flexDirection="column"
            alignItems="center"
            textAlign="left"
            className={classes.detailsListItem}
          >
            <Typography
              variant="subtitle2"
              style={{
                width: '100%',
              }}
              className={classes.detailsListText}
            >
              {`${i18n.t('common.description')}:`}
            </Typography>
            <Typography
              variant="body2"
              style={{
                marginTop: 5,
                width: '100%',
              }}
              className={classes.detailsListText}
            >
              {document.description}
            </Typography>
          </Box>
        )}
        {tags.length > 0 && (
          <Box mt={2} pb={1} pt={1} display="flex" flexDirection="row" alignItems="flex-start">
            <Typography variant="subtitle2" className={classes.tagsText}>
              {`${i18n.t('documents.tags')}:`}
            </Typography>
            <Box className={classes.chipContainer} ml={0.5}>
              {tags.map(({ label }) => (
                <Chip
                  key={label}
                  label={label}
                  clickable
                  color="primary"
                  onClick={() => onTagClick(label)}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
}

DocumentDetailsWidget.propTypes = {
  /**
   * Document to be showed
   */
  document: PropTypes.any,
  tags: PropTypes.array,
  onTagClick: PropTypes.func,
};

DocumentDetailsWidget.defaultProps = {
  tags: [],
  document: {},
  onTagClick: () => {},
};

export default DocumentDetailsWidget;
